/*
 * The People screen's state.
 *
 * Contact details are masked in the list and only the per-member call returns a real phone number
 * or email — the DTO comment says so explicitly. This hook keeps that shape: `reveal` fetches one
 * member on demand and the revealed record is held apart from the list, so nothing can accidentally
 * render a real number for every row.
 *
 * `coverage` is the derivation the frame's staffing panel needs. It is by department rather than by
 * hour, because there is no shift roster in this product — see the gap note on the screen.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, opsApi } from '../../../api';
import type {
  OpsDepartmentDto, OpsStaffInput, OpsStaffMemberDto, OpsStaffStatus, OpsStaffSummaryDto,
} from '../../../api';

export interface DepartmentCoverage {
  department: OpsDepartmentDto;
  active: number;
  standby: number;
  offDuty: number;
  total: number;
  hasLead: boolean;
}

export function coverageOf(
  departments: readonly OpsDepartmentDto[],
  staff: readonly OpsStaffSummaryDto[],
): DepartmentCoverage[] {
  return departments.map((department) => {
    const members = staff.filter((s) => s.departments.some((d) => d.departmentId === department.id));
    return {
      department,
      active: members.filter((m) => m.status === 'active').length,
      standby: members.filter((m) => m.status === 'standby').length,
      offDuty: members.filter((m) => m.status === 'off_duty').length,
      total: members.length,
      hasLead: members.some((m) => m.departments.some((d) => d.departmentId === department.id && d.isLead)),
    };
  });
}

export interface OpsPeopleState {
  staff: OpsStaffSummaryDto[];
  departments: OpsDepartmentDto[];
  coverage: DepartmentCoverage[];
  loading: boolean;
  loadError: string;
  error: string;
  busyId: string | null;
  /** The one member whose real contact details were fetched, if any. */
  revealed: OpsStaffMemberDto | null;
  revealBusy: boolean;
  reveal: (id: string) => Promise<void>;
  clearRevealed: () => void;
  create: (input: OpsStaffInput) => Promise<void>;
  update: (member: OpsStaffSummaryDto, patch: Partial<OpsStaffInput>) => Promise<void>;
  setStatus: (member: OpsStaffSummaryDto, status: OpsStaffStatus) => Promise<void>;
  remove: (member: OpsStaffSummaryDto) => Promise<void>;
  /*
   * Departments. The endpoints have existed since TICK-121 and the classic panel used all four;
   * the rebuilt screen read the list to group people by it and offered no way to change it, so on
   * a new event every member stayed unassigned for ever. Added here rather than in the screen so
   * both shells would share them if the classic panel ever came back.
   */
  createDepartment: (name: string) => Promise<void>;
  renameDepartment: (department: OpsDepartmentDto, name: string) => Promise<void>;
  removeDepartment: (department: OpsDepartmentDto) => Promise<void>;
  /** Replaces the member's whole set — the endpoint is a PUT, not a patch. */
  setDepartments: (
    member: OpsStaffSummaryDto,
    departments: { departmentId: string; isLead?: boolean }[],
  ) => Promise<void>;
  reload: () => Promise<void>;
}

export function useOpsPeople(token: string, eventSlug?: string): OpsPeopleState {
  const [staff, setStaff] = useState<OpsStaffSummaryDto[] | null>(null);
  const [departments, setDepartments] = useState<OpsDepartmentDto[]>([]);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<OpsStaffMemberDto | null>(null);
  const [revealBusy, setRevealBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([
        opsApi.listStaff(token, eventSlug),
        opsApi.listDepartments(token, eventSlug).catch(() => [] as OpsDepartmentDto[]),
      ]);
      setStaff(s);
      setDepartments(d);
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดข้อมูลทีมงานไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (id: string | null, fn: () => Promise<unknown>, fallback: string) => {
    setBusyId(id);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusyId(null);
    }
  }, [load]);

  const list = staff ?? [];

  return {
    staff: list, departments,
    coverage: useMemo(() => coverageOf(departments, list), [departments, list]),
    loading: staff === null && !loadError,
    loadError, error, busyId, revealed, revealBusy,
    reveal: async (id) => {
      setRevealBusy(true);
      setError('');
      try {
        setRevealed(await opsApi.getStaffMember(token, id, eventSlug));
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'ดูข้อมูลติดต่อไม่สำเร็จ');
      } finally {
        setRevealBusy(false);
      }
    },
    clearRevealed: () => setRevealed(null),
    create: (input) => run(null, () => opsApi.createStaffMember(token, input, eventSlug), 'เพิ่มสมาชิกไม่สำเร็จ'),
    update: (member, patch) =>
      run(member.id, () => opsApi.updateStaffMember(token, member.id, { ...patch, version: member.version }, eventSlug), 'บันทึกไม่สำเร็จ'),
    setStatus: (member, status) =>
      run(member.id, () => opsApi.updateStaffMember(token, member.id, { status, version: member.version }, eventSlug), 'เปลี่ยนสถานะไม่สำเร็จ'),
    remove: (member) =>
      run(member.id, () => opsApi.deleteStaffMember(token, member.id, member.version, eventSlug), 'ลบสมาชิกไม่สำเร็จ'),
    createDepartment: (name) =>
      run(null, () => opsApi.createDepartment(token, { name }, eventSlug), 'สร้างฝ่ายไม่สำเร็จ'),
    renameDepartment: (department, name) =>
      run(department.id, () => opsApi.updateDepartment(
        token, department.id, { name, version: department.version }, eventSlug,
      ), 'เปลี่ยนชื่อฝ่ายไม่สำเร็จ'),
    removeDepartment: (department) =>
      run(department.id, () => opsApi.deleteDepartment(
        token, department.id, department.version, eventSlug,
      ), 'ลบฝ่ายไม่สำเร็จ'),
    setDepartments: (member, departments) =>
      run(member.id, () => opsApi.setStaffDepartments(token, member.id, departments, eventSlug),
        'บันทึกฝ่ายของสมาชิกไม่สำเร็จ'),
    reload: load,
  };
}
